import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { SavedSearchService } from 'src/app/_shared/service/saved-search.service';
import { UserState } from 'src/app/_shared/state/user.state';
import { AppState } from 'src/app/app.state';
import { ClientDashboardState } from 'src/app/client-dashboard/state/client-dashboard.state';
import { PropertyFilterService } from 'src/app/property-filter/service/property-filter.service';
import { SavedSearchState } from '../../_shared/state/saved-search.state';
import { ConfirmDialogComponent } from '../../shared/modules/alerts/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-manage-saved-searches',
  templateUrl: './manage-saved-searches.component.html',
  styleUrls: ['./manage-saved-searches.component.scss'],
})
export class ManageSavedSearchesComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  filteredData: any;
  filteredList: any = [];
  filterValue;
  dataSource: MatTableDataSource<Array<any>>;
  savedSearchList$: Observable<Array<any>>;
  savedSearchList: Array<any> = [];
  savedList: any;

  selection;
  isDataLoading = true;
  showAppNav = true;
  @Output() clickMenuEvent = new EventEmitter<any>();
  onDestroyNotifier$ = new Subject();

  constructor(
    private savedSearchState: SavedSearchState,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private authorizeService: AuthorizeService,
    private savedSearchService: SavedSearchService,
    private userState: UserState,
    private cdr: ChangeDetectorRef,
    private appState: AppState,
    public propertyFilterService: PropertyFilterService,
    public clientDashboardState: ClientDashboardState
  ) { }

  ngOnInit() {
    if (!this.authorizeService.canAct('savedFilter')) {
      return;
    }
    if (this.router.url === '/dashboard') {
      this.showAppNav = false;
    }
    this.savedSearchService.getSavedItems();
    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.savedSearchState.$activeSaveSearches
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe((savedSearchList) => {
          this.savedSearchList = this.getFormattedSavedSearchList(savedSearchList);
          this.cdr.detectChanges();

          this.dataSource = new MatTableDataSource(savedSearchList);
          this.dataSource.paginator = this.paginator;
          this.savedSearchList$ = this.dataSource.connect();
          this.isDataLoading = false;
        });
    });
    this.savedSearchService.getSavedSearches();
  }

  getFormattedSavedSearchList(savedSearchList) {
    for (let i = 0; i < savedSearchList.length; i++) {
      if (savedSearchList[i].appliedFilter?.filters) {
        let savedList = this.propertyFilterService.getFilterDisplayList(savedSearchList[i].appliedFilter?.filters);
        savedSearchList[i]['filterList'] = savedList;
      }
    }
    return savedSearchList;
  }

  goToHome() {
    this.router.navigate(['workspace']);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.slNo + 1}`;
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  loadSearch(property) {
    if (!this.authorizeService.canAct('savedFilter')) {
      return false;
    }
    if (property?.appliedFilter?.filters) {
      let location = property?.placeDetails?.fullAddress;
      this.propertyFilterService.setPrimaryFilterToUrl(property.appliedFilter.filters, location || null);
    }
  }

  goToParentPath() {
    const navigatedTo = this.appState.navigatedFrom;
    navigatedTo
      ? navigatedTo === 'dashboard'
        ? this.clickMenuEvent.emit('dashboard')
        : this.router.navigate([navigatedTo])
      : this.clickMenuEvent.emit('dashboard');
  }

  deleteSavedData(data) {
    if (!this.authorizeService.canAct('savedFilter')) {
      return false;
    }
    if (!data.id) {
      this.openSnackBar('Something went wrong', 'snackbar-error');
      return false;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
      // panelClass: 'custom-modalbox',
      data: {
        type: 'deleteSaveSearch',
        message: 'Please note, this will delete the data permanantly',
        title: 'Do you want to continue?',
        proceedAction: (status, callBack) => {
          if (status) {
            this.savedSearchService
              .deleteSavedSearch(data)
              .pipe(takeUntil(this.onDestroyNotifier$))
              .subscribe(
                (res) => {
                  this.openSnackBar(res['message'], 'snackbar-success');
                  callBack({ status: true });
                },
                (err) => {
                  this.openSnackBar(err['message'], 'snackbar-error');
                  dialogRef.close();
                }
              );
          } else {
            return false;
          }
        },
        proceedCancelAction(cancelCallBack) { },
      },
    });
  }

  ngOnDestroy() {
    this.dataSource?.disconnect();
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }
}
